import { computed, inject, Injectable, signal } from '@angular/core'
import { Observable, map, forkJoin, switchMap, from } from 'rxjs'
import { ChatMessage, ChatSession } from '@models/chat.model'
import { AssetChatApiService, SendMessageRequest } from './asset-chat-api.service'
import { AuthService } from '@auth0/auth0-angular'
import { GeneralChatApiService } from './general-chat-api.service'
import { ToasterService } from './toaster.service'
import { PublisherHelperService } from './publisher-helper.service'
import { MediaAssetHelperService } from './media-asset-helper.service'
import { PlaylistHelperService } from './playlist-helper.service'

@Injectable({
  providedIn: 'root',
})
export class ChatHelperService {
  private assetChatApiService = inject(AssetChatApiService)
  private generalChatApiService = inject(GeneralChatApiService)
  private authService = inject(AuthService)
  private toasterService = inject(ToasterService)
  private publisherHelperService = inject(PublisherHelperService)
  private mediaAssetHelperService = inject(MediaAssetHelperService)
  private playlistHelperService = inject(PlaylistHelperService)

  private chatSessions = signal<ChatSession[]>([])
  private currentSession = signal<ChatSession | null>(null)

  getChatSessions = this.chatSessions.asReadonly()
  getCurrentSession = this.currentSession.asReadonly()

  currentUserId = computed(() => {
    var userId = this.authService.user$.subscribe(data => {
      return data?.sub
    })

    return userId
  })

  getAllChats(): Observable<ChatSession[]> {
    return forkJoin({
      assetChats: this.assetChatApiService.getAssetChats$(),
      generalChats: this.generalChatApiService.getGeneralChats$(),
    }).pipe(
      map(({ assetChats, generalChats }) => {
        const assetChatsList = assetChats.chats ? assetChats.chats : []
        const generalChatsList = generalChats.chats || []
        const allChats = [...assetChatsList, ...generalChatsList]

        allChats.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateB - dateA
        })

        this.chatSessions.set(allChats)
        return allChats
      })
    )
  }

  getAssetChats(assetId: string): ChatSession[] {
    return this.chatSessions().filter(session => session.mediaAssetId === assetId)
  }

  getGeneralChats(): ChatSession[] {
    return this.chatSessions().filter(session => !session.mediaAssetId)
  }

  createGeneralChat(assetId?: string, projectId?: string): Observable<ChatSession> {
    return this.generalChatApiService.createGeneralChat$({ mediaAssetId: assetId, projectId: projectId }).pipe(
      map(data => {
        if (data.success) {
          this.chatSessions.update(sessions => {
            const updatedSessions = [...sessions, data.chat]
            return updatedSessions.sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime()
              const dateB = new Date(b.createdAt).getTime()
              return dateB - dateA
            })
          })
          return data.chat
        }
        throw new Error('Failed to create asset chat')
      })
    )
  }

  createAssetChat(assetId: string, projectId?: string): Observable<ChatSession> {
    return this.assetChatApiService.createAssetChat$({ mediaAssetId: assetId, projectId: projectId }).pipe(
      map(data => {
        if (data.success) {
          this.chatSessions.update(sessions => {
            const updatedSessions = [...sessions, data.chat]
            return updatedSessions.sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime()
              const dateB = new Date(b.createdAt).getTime()
              return dateB - dateA
            })
          })
          return data.chat
        }
        throw new Error('Failed to create asset chat')
      })
    )
  }

  selectSession(sessionId: string): void {
    const session = this.chatSessions().find(s => s.id === sessionId)
    if (session) {
      this.currentSession.set(session)
    } else {
      this.currentSession.set(null)
    }
  }

  refreshAndSelectSession(sessionId: string): Observable<ChatSession | null> {
    const sessions = this.chatSessions()
    const session = sessions.find(s => s.id === sessionId)

    if (!session && sessions.length === 0) {
      return this.getAllChats().pipe(
        switchMap(() => {
          const updatedSessions = this.chatSessions()
          const foundSession = updatedSessions.find(s => s.id === sessionId)
          const isAssetChat = foundSession?.mediaAssetId !== undefined && foundSession?.mediaAssetId !== null
          const getSingleChatObservable = isAssetChat
            ? this.assetChatApiService.getSingleChat$(sessionId)
            : this.generalChatApiService.getSingleChat$(sessionId)

          return getSingleChatObservable.pipe(
            map(response => {
              if (response.success && response.chat) {
                this.updateChatSessionById(sessionId, response.chat)
                this.currentSession.set(response.chat)
                return response.chat
              }
              return null
            })
          )
        })
      )
    }

    const isAssetChat = session?.mediaAssetId !== undefined && session?.mediaAssetId !== null

    const getSingleChatObservable = isAssetChat
      ? this.assetChatApiService.getSingleChat$(sessionId)
      : this.generalChatApiService.getSingleChat$(sessionId)

    return getSingleChatObservable.pipe(
      map(response => {
        if (response.success && response.chat) {
          this.updateChatSessionById(sessionId, response.chat)
          this.currentSession.set(response.chat)
          return response.chat
        }
        return null
      })
    )
  }

  async sendMessageStreaming(message: string, onEvent: (event: any) => void, onComplete?: () => void) {
    const currentSession = this.currentSession()
    if (!currentSession) return

    const sendMessageRequest: SendMessageRequest = {
      message: message,
    }

    if (currentSession.mediaAssetId) {
      try {
        const token = await new Promise<string>((resolve, reject) => {
          this.authService.getAccessTokenSilently().subscribe({
            next: (token) => resolve(token),
            error: (err) => reject(err)
          })
        })

        const generator = this.assetChatApiService.sendMessageStreaming(currentSession.id, sendMessageRequest, token)

        for await (const line of generator) {
          try {
            const event = JSON.parse(line)
            onEvent(event)

            if (event.type === 'result' && event.chat) {
              this.updateChatSessionById(currentSession.id, event.chat)
              this.handlePostMessageActions(event.chat)
            }
          } catch (e) {
            console.error('Error parsing SSE line:', line)
          }
        }
      } catch (error) {
        console.error('Error in streaming message:', error)
      } finally {
        if (onComplete) onComplete()
      }
    } else {
      // General chat streaming not implemented yet, fallback to regular sendMessage
      this.sendMessage(message, onComplete)
    }
  }

  private handlePostMessageActions(chat: ChatSession) {
    const lastMessage = chat.messages[chat.messages.length - 1]
    if (lastMessage && lastMessage.role === 'assistant') {
      const messageType = lastMessage.message_type
      const clipRelatedTypes = ['clip_standard', 'clip_promo_trailer', 'clip_rollover', 'clip_follow_up']

      const shouldReloadAssets =
        clipRelatedTypes.includes(messageType) ||
        messageType.includes('clip') ||
        messageType.includes('rollover')

      if (shouldReloadAssets) {
        this.mediaAssetHelperService.loadAllAssets().subscribe()
      }

      if (messageType === 'clip_promo_trailer') {
        const playlistIds = chat.playlistIds
        if (playlistIds && playlistIds.length > 0) {
          const lastPlaylistId = playlistIds[playlistIds.length - 1]
          this.playlistHelperService.refreshPlaylist(lastPlaylistId)
        }
      }
    }
  }

  sendMessage(message: string, onComplete?: () => void): void {
    const currentSession = this.currentSession()
    if (!currentSession) return

    var sendMessageRequest: SendMessageRequest = {
      message: message,
    }

    if (currentSession.mediaAssetId) {
      this.assetChatApiService.sendMessage$(currentSession.id, sendMessageRequest).subscribe({
        next: response => {
          if (response.success && response.chat) {
            this.updateChatSessionById(currentSession.id, response.chat)
            this.handlePostMessageActions(response.chat)
          }
          if (onComplete) onComplete()
        },
        error: () => {
          if (onComplete) onComplete()
        },
      })
    } else {
      const selectedPublishers = this.publisherHelperService.getSelectedPublishers()
      const publisherIds = selectedPublishers.map(p => p.id)

      const selectedSources = [...publisherIds]

      sendMessageRequest.selectedSources = selectedSources

      this.generalChatApiService.sendMessage$(currentSession.id, sendMessageRequest).subscribe({
        next: response => {
          if (response.success && response.chat) {
            this.updateChatSessionById(currentSession.id, response.chat)
            this.handlePostMessageActions(response.chat)
          }
          if (onComplete) onComplete()
        },
        error: () => {
          if (onComplete) onComplete()
        },
      })
    }
  }

  private updateChatSessionById(sessionId: string, updatedChat: ChatSession): void {
    this.chatSessions.update(sessions => {
      const updatedSessions = sessions.map(s => (s.id === sessionId ? updatedChat : s))
      return updatedSessions.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA
      })
    })

    if (this.currentSession()?.id === sessionId) {
      this.currentSession.set(updatedChat)
    }
  }

  deleteSession(sessionId: string): void {
    const session = this.chatSessions().find(s => s.id === sessionId)
    if (!session) return

    const isAssetChat = session.mediaAssetId !== undefined && session.mediaAssetId !== null

    const deleteObservable = isAssetChat
      ? this.assetChatApiService.deleteChat$(sessionId)
      : this.generalChatApiService.deleteChat$(sessionId)

    deleteObservable.subscribe({
      next: response => {
        if (response.success) {
          this.chatSessions.update(sessions => sessions.filter(s => s.id !== sessionId))

          if (this.currentSession()?.id === sessionId) {
            const remainingSessions = this.chatSessions()
            this.currentSession.set(remainingSessions.length > 0 ? remainingSessions[0] : null)
          }

          this.toasterService.success(response.message || 'Chat deleted successfully')
        } else {
          this.toasterService.error(response.message || 'Failed to delete chat')
        }
      },
      error: error => {
        console.error('Error deleting chat:', error)
        this.toasterService.error('Failed to delete chat')
      },
    })
  }

  updateCurrentSession(updatedSession: ChatSession): void {
    this.chatSessions.update(sessions => {
      const updatedSessions = sessions.map(s => (s.id === updatedSession.id ? updatedSession : s))
      return updatedSessions.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA
      })
    })

    this.currentSession.set(updatedSession)
  }

  addMessageToCurrentSession(message: ChatMessage): void {
    const current = this.currentSession()
    if (!current) return

    const updatedSession = {
      ...current,
      messages: [...current.messages, message],
    }

    this.chatSessions.update(sessions => {
      return sessions.map(s => (s.id === current.id ? updatedSession : s))
    })

    this.currentSession.set(updatedSession)
  }

  renameChatTitle(chatId: string, newTitle: string, isAssetChat: boolean): Observable<ChatSession> {
    const request = { title: newTitle }

    const updateObservable = isAssetChat
      ? this.assetChatApiService.updateChatTitle$(chatId, request)
      : this.generalChatApiService.updateChatTitle$(chatId, request)

    return updateObservable.pipe(
      switchMap(response => {
        if (response.success) {
          if (response.chat) {
            this.updateChatSessionById(chatId, response.chat)
            this.toasterService.success(response.message)
            return [response.chat]
          } else {
            const currentChat = this.chatSessions().find(s => s.id === chatId)
            if (currentChat) {
              const updatedChat = { ...currentChat, title: newTitle }
              this.updateChatSessionById(chatId, updatedChat)
              this.toasterService.success(response.message)
              return [updatedChat]
            }
          }
        }
        if (response.message) {
          this.toasterService.error(response.message)
          throw new Error(response.message)
        } else {
          this.toasterService.error('Failed to update chat title')
          throw new Error('Failed to update chat title')
        }
      })
    )
  }

  shareChatSnapshot(chatId: string, targetUserIds: string[]): Observable<any> {
    const request = { targetUserIds }

    return this.assetChatApiService.shareChatSnapshot$(chatId, request).pipe(
      map(response => {
        if (response.success) {
          return response
        } else {
          throw new Error(response.message || 'Failed to share chat snapshot')
        }
      })
    )
  }

  isMediaAssetChat(session?: ChatSession | null): boolean {
    const chat = session ?? this.currentSession()
    return chat?.mediaAssetId !== undefined && chat?.mediaAssetId !== null
  }

  isGeneralChat(session?: ChatSession | null): boolean {
    const chat = session ?? this.currentSession()
    return !this.isMediaAssetChat(chat)
  }

  getCurrentChatType(): 'media-asset' | 'general' | null {
    const session = this.currentSession()
    if (!session) return null
    return this.isMediaAssetChat(session) ? 'media-asset' : 'general'
  }
}
